/** ICP-000003 — Development Environment Bootstrap (mirrors pkg/devenvbootstrap) */

export const ICP_DEVENV_ID = 'ICP-000003';
export const ICP_DEVENV_NAME = 'Development Environment Bootstrap';
export const ICP_DEVENV_LAYER = 'Foundation';
export const ICP_DEVENV_PRIORITY = 'P0';
export const ICP_NEXT_PACKAGE = 'ICP-000004';

export interface TeamMemberRow {
  role: string;
  responsibility: string;
  focus: string;
}

export interface PrerequisiteRow {
  tool: string;
  version: string;
  required: boolean;
  check: string;
}

export interface LocalServiceRow {
  service: string;
  port: string;
  image: string;
  status: string;
}

export interface EnvironmentConfigRow {
  variable: string;
  required: boolean;
  example: string;
  secret: boolean;
}

export interface IDEToolingRow {
  tool: string;
  config: string;
  description: string;
}

export interface BootstrapStepRow {
  step: number;
  action: string;
  command: string;
  status: string;
}

export interface CIParityRow {
  check: string;
  local: string;
  ci: string;
  parity: boolean;
}

export interface ExecutiveBootstrapRow {
  metric: string;
  value: string;
  status: string;
  score: number;
}

export const DEPENDS_ON = ['ICP-000001', 'ICP-000002'];
export const IMPLEMENTS = ['FCP', 'BCP', 'APP', 'EDP', 'SDP', 'MPC', 'EGB', 'MDEP', 'MDDM'];

export const BOOTSTRAP_TEAM = [
  'Chief Technology Officer', 'Platform Engineering Lead', 'DevOps Architect',
  'Backend Lead', 'Frontend Lead', 'Database Architect', 'Security Architect',
  'QA Lead', 'Documentation Architect',
];

export const PREREQUISITES = [
  'Go 1.23+', 'Node 20 LTS', 'npm 10', 'Docker 27+', 'Docker Compose v2',
  'Git 2.x', 'Make', 'PostgreSQL Client 17', 'Redis CLI', 'curl',
];

export const LOCAL_SERVICES = ['PostgreSQL 17', 'Redis 7', 'Nginx', 'Prometheus', 'Grafana'];

export const ENVIRONMENT_CONFIG = [
  'DATABASE_URL', 'REDIS_URL', 'JWT_SECRET', 'API_BASE_URL', 'LOG_LEVEL',
  'FEATURE_FLAGS', 'MARKETPLACE_CREDENTIALS', 'AI_PROVIDER_KEYS', 'TENANT_DEFAULT',
];

export const IDE_TOOLING = [
  'VS Code Extensions', 'gopls', 'ESLint', 'Prettier', 'Tailwind IntelliSense',
  'Dev Container', 'Launch Configurations', 'Tasks JSON', 'Graphify CLI',
];

export const MAKEFILE_TARGETS = [
  'test', 'lint', 'build', 'web-install', 'web-build', 'web-lint',
  'run-platform', 'run-auth', 'deploy-check', 'clean',
];

export const BOOTSTRAP_WORKFLOW = [
  'Clone Repository', 'Install Prerequisites', 'Copy Env Template',
  'Start Docker Compose', 'Run Migrations', 'Seed Reference Data',
  'Install Frontend Deps', 'Verify Health Checks', 'Run Smoke Tests',
  'Confirm Bootstrap Complete',
];

export const CI_PARITY_CHECKS = [
  'go vet', 'go test', 'npm lint', 'typescript check', 'docker build',
  'security scan', 'dependency audit', 'openapi validation',
];

export const READINESS_GATES = [
  'Prerequisites Installed', 'Services Healthy', 'Migrations Applied',
  'Env Configured', 'Lint Passes', 'Tests Pass', 'Smoke Tests Pass',
];

export const SECRETS_POLICY = [
  'No Secrets in Repo', 'Env Example Only', 'Vault for Production',
  'Local .env Gitignored', 'Rotate on Leak',
];

export const OBSERVABILITY_BOOTSTRAP = [
  'Structured JSON Logging', 'Health Endpoints', 'Prometheus Metrics',
  'Grafana Dashboards', 'Correlation IDs',
];

export const ARCHITECTURE_STAGES = [
  'Bootstrap Team', 'Prerequisites', 'Local Services', 'Environment Config',
  'IDE Tooling', 'Bootstrap Workflow', 'CI Parity', 'Executive Bootstrap',
];

export const LIVE_ROUTES: Record<string, string> = {
  codingConstitution: '/platform/coding-constitution',
  repositoryBlueprint: '/platform/repository-blueprint',
  enterpriseDevOps: '/platform/enterprise-devops',
  engineeringGovernance: '/platform/engineering-governance',
};

export const DEMO_TEAM: TeamMemberRow[] = [
  { role: 'Platform Engineering Lead', responsibility: 'Bootstrap script ownership', focus: 'Makefile & Docker Compose' },
  { role: 'DevOps Architect', responsibility: 'CI parity and pipeline alignment', focus: 'GitHub Actions gates' },
  { role: 'Backend Lead', responsibility: 'Go workspace and service startup', focus: 'go.work, make run-*' },
  { role: 'Frontend Lead', responsibility: 'Node toolchain and Vite dev server', focus: 'npm, web-lint' },
  { role: 'Database Architect', responsibility: 'Migration and seed bootstrap', focus: 'PostgreSQL 17 local' },
  { role: 'Security Architect', responsibility: 'Secrets policy enforcement', focus: '.env.example, no secrets in repo' },
];

export const DEMO_PREREQUISITES: PrerequisiteRow[] = [
  { tool: 'Go', version: '1.23+', required: true, check: 'go version' },
  { tool: 'Node.js', version: '20 LTS', required: true, check: 'node --version' },
  { tool: 'Docker', version: '27+', required: true, check: 'docker --version' },
  { tool: 'Docker Compose', version: 'v2', required: true, check: 'docker compose version' },
  { tool: 'Git', version: '2.x', required: true, check: 'git --version' },
  { tool: 'Make', version: 'any', required: true, check: 'make --version' },
];

export const DEMO_LOCAL_SERVICES: LocalServiceRow[] = [
  { service: 'PostgreSQL', port: '5432', image: 'postgres:17', status: 'required' },
  { service: 'Redis', port: '6379', image: 'redis:7-alpine', status: 'required' },
  { service: 'Nginx', port: '80/443', image: 'nginx:1.27', status: 'optional' },
  { service: 'Prometheus', port: '9090', image: 'prom/prometheus:v2', status: 'optional' },
  { service: 'Grafana', port: '3000', image: 'grafana/grafana:11', status: 'optional' },
];

export const DEMO_ENV_CONFIG: EnvironmentConfigRow[] = [
  { variable: 'DATABASE_URL', required: true, example: 'postgres://localhost:5432/aicos', secret: false },
  { variable: 'REDIS_URL', required: true, example: 'redis://localhost:6379', secret: false },
  { variable: 'JWT_SECRET', required: true, example: 'generate-with-openssl', secret: true },
  { variable: 'API_BASE_URL', required: true, example: 'http://localhost:8080', secret: false },
  { variable: 'LOG_LEVEL', required: false, example: 'debug', secret: false },
  { variable: 'AI_PROVIDER_KEY', required: false, example: 'sk-...', secret: true },
];

export const DEMO_IDE_TOOLING: IDEToolingRow[] = [
  { tool: 'VS Code', config: '.vscode/extensions.json', description: 'Go, ESLint, Tailwind, Prettier' },
  { tool: 'Dev Container', config: '.devcontainer/devcontainer.json', description: 'Reproducible dev environment' },
  { tool: 'Launch Config', config: '.vscode/launch.json', description: 'Debug Go services and Vite' },
  { tool: 'Graphify CLI', config: 'graphify-out/', description: 'Codebase knowledge graph' },
  { tool: 'Makefile', config: 'Makefile', description: 'test, lint, build, run-* targets' },
  { tool: 'Go Workspace', config: 'go.work', description: 'Multi-module Go workspace' },
];

export const DEMO_BOOTSTRAP_WORKFLOW: BootstrapStepRow[] = [
  { step: 1, action: 'Clone repository', command: 'git clone ...', status: 'manual' },
  { step: 2, action: 'Copy env template', command: 'cp .env.example .env', status: 'manual' },
  { step: 3, action: 'Start local services', command: 'docker compose up -d', status: 'automated' },
  { step: 4, action: 'Run migrations', command: 'make migrate', status: 'automated' },
  { step: 5, action: 'Install frontend deps', command: 'make web-install', status: 'automated' },
  { step: 6, action: 'Verify health', command: 'curl /health', status: 'automated' },
  { step: 7, action: 'Run smoke tests', command: 'make test', status: 'automated' },
  { step: 8, action: 'Confirm bootstrap', command: 'bootstrap checklist', status: 'gate' },
];

export const DEMO_CI_PARITY: CIParityRow[] = [
  { check: 'Go vet', local: 'make lint', ci: 'go vet ./...', parity: true },
  { check: 'Go test', local: 'make test', ci: 'go test ./...', parity: true },
  { check: 'TypeScript', local: 'make web-lint', ci: 'npm run lint', parity: true },
  { check: 'Docker build', local: 'docker build', ci: 'docker/build-push-action', parity: true },
  { check: 'Security scan', local: 'gosec', ci: 'github/codeql-action', parity: true },
];

export const DEMO_EXECUTIVE: ExecutiveBootstrapRow[] = [
  { metric: 'Prerequisites Defined', value: '10 tools', status: 'complete', score: 100 },
  { metric: 'Local Services', value: '5 services', status: 'configured', score: 95 },
  { metric: 'Bootstrap Steps', value: '10 steps', status: 'documented', score: 100 },
  { metric: 'CI Parity Checks', value: '8 checks', status: 'aligned', score: 92 },
  { metric: 'Readiness Gates', value: '7 gates', status: 'enforced', score: 100 },
  { metric: 'P0 Bootstrap Ready', value: 'foundation', status: 'approved', score: 98 },
];

export function buildBootstrapDashboard() {
  return {
    prerequisites: PREREQUISITES.length,
    local_services: LOCAL_SERVICES.length,
    environment_vars: ENVIRONMENT_CONFIG.length,
    bootstrap_steps: BOOTSTRAP_WORKFLOW.length,
    ci_parity_checks: CI_PARITY_CHECKS.length,
    readiness_gates: READINESS_GATES.length,
  };
}
